import { AfterViewInit, Component, ElementRef, Input } from '@angular/core';

@Component({
    selector: 'finder-block-wrapper',
    styleUrls: ['./block-wrapper.component.scss'],
    template: `<ng-content></ng-content>`,
    standalone: true,
})
export class BlockWrapperComponent implements AfterViewInit {
    @Input() blockData: any;

    constructor(
        private elementRef: ElementRef,
    ) {}

    ngAfterViewInit(): void {
        this.createCss(this.blockData?.afterMedia);
    }

    createCss(afterMedia) {
        if (!afterMedia) {
            return;
        }

        const parentEl = this.elementRef.nativeElement.parentElement;

        if (!Array.isArray(afterMedia)) {
            // Desktop
            const desktop = afterMedia.desktop;
            parentEl.style.setProperty('--after-image-bg', desktop.url);
            parentEl.style.setProperty('--after-image-height', desktop.height);

            // Mobile
            const mobile = afterMedia.mobile;
            parentEl.style.setProperty('--after-image-mobile-bg', mobile.url);
            parentEl.style.setProperty('--after-image-mobile-height', mobile.height);
        } else {
            // Reduce arrays to a string of urls and get the largest height
            const { desktopUrl, desktopHeight, mobileUrl, mobileHeight } = this.getMediaValues(afterMedia);

            // Desktop
            parentEl.style.setProperty('--after-image-bg', desktopUrl);
            parentEl.style.setProperty('--after-image-height', desktopHeight);

            // Mobile
            parentEl.style.setProperty('--after-image-mobile-bg', mobileUrl);
            parentEl.style.setProperty('--after-image-mobile-height', mobileHeight);
        }

        // Check for additional properties
        const props = [
            'afterPosition',
            'afterPositionMobile',
            'afterRepeat',
            'afterRepeatMobile',
            'afterMargin',
            'afterMarginMobile',
            'afterSize',
            'afterSizeMobile',
            'afterInset',
            'afterInsetMobile',
            'afterZIndex'
        ];
        const propMap = {
            afterPosition: '--after-image-bg-position',
            afterPositionMobile: '--after-image-mobile-bg-position',
            afterRepeat: '--after-image-bg-repeat',
            afterRepeatMobile: '--after-image-mobile-bg-repeat',
            afterMargin: '--after-image-margin',
            afterMarginMobile: '--after-image-mobile-margin',
            afterSize: '--after-image-bg-size',
            afterSizeMobile: '--after-image-mobile-bg-size',
            afterInset: '--after-image-inset',
            afterInsetMobile: '--after-image-mobile-inset',
            afterZIndex: '--after-image-z-index'
        };

        props.forEach((prop) => {
            // Get value with possible fallback for mobile
            let propValue = this.blockData[prop];
            if (!propValue && prop.endsWith('Mobile')) {
                propValue = this.blockData[prop.replace('Mobile', '')];
            }

            // Don't bother if there is no value
            if (!propValue) {
                return;
            }

            parentEl.style.setProperty(propMap[prop], propValue);
        });
    }

    private getMediaValues(obj) {
        let desktopHeight = 0;
        let mobileHeight = 0;
        let desktopHeightWithUnit;
        let mobileHeightWithUnit;

        return obj.reduce((acc, item, idx) => {
            const desktop = item.desktop;
            const mobile = item.mobile;

            if (!desktop) {
                return acc;
            }

            // Get media max height
            const dh = Number(desktop.height.replace(/px|%/g, ''));
            if (dh > desktopHeight) {
                desktopHeight = dh;
                desktopHeightWithUnit = desktop.height;
            }

            const mh = Number(mobile.height.replace(/px|%/g, ''));
            if (mh > mobileHeight) {
                mobileHeight = mh;
                mobileHeightWithUnit = mobile.height;
            }

            // Add commas
            if (acc.desktopUrl) {
                acc.desktopUrl += ',';
            } else {
                acc.desktopUrl = '';
            }
            if (acc.mobileUrl) {
                acc.mobileUrl += ',';
            } else {
                acc.mobileUrl = '';
            }

            // Set urls and height
            acc.desktopUrl += desktop.url;
            acc.desktopHeight = desktopHeightWithUnit ?? '0';
            acc.mobileUrl += mobile.url;
            acc.mobileHeight = mobileHeightWithUnit ?? '0';

            return acc;
        }, {});
    }
}
